import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import Auth from "../../../Auth";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { buildScenesForSave, serializeScenes } from "../storyEnginePayload";

// Module-level state that survives unmount/remount. When flush() fires an
// async PUT on unmount, the next mount's GET may return the old server value
// before the PUT lands. This bridges that gap (SW-10355).
let _pendingSave = undefined; // { projectId, isActive }

export default ({ projectId, isActive, seed, characters, synopsis, outline, metadata, scenes, setSaveError, setFeaturedStyles, stripLegacyStyleFromSave = false }) => {
  const [loadingState, setLoadingState] = useState(true);
  const saved = useStoryBibleStore((state) => state.saved);
  const setSaved = useStoryBibleStore((state) => state.setSaved);
  const setIsLoadingSceneToWrite = useStoryBibleStore((state) => state.setIsLoadingSceneToWrite);
  const loading = seed === null;

  const owner = useStoryBibleStore((state) => state.owner);
  const sessionRef = useRef(null);

  const errorMap = [
    {
      matches: "maximum allowed size",
      message: "Story Bible too large. Please reduce the size of your Story Bible to continue saving.",
      retry: false,
      persistent: true,
    },
    {
      matches: "contains an invalid nested entity",
      message: "Story Bible too large. Please reduce the size of your Story Bible to continue saving.",
      retry: false,
      persistent: true,
    },
  ];

  // Loads story engine from backend
  useEffect(() => {
    const store = useStoryBibleStore.getState();
    const owner = store.acquireOwner(projectId);
    const session = { owner, engineId: null, latest: null, revision: 0, lastSavedScenesJson: null, active: true, hydrated: false };
    sessionRef.current = session;
    setLoadingState(true);
    const isCurrent = () => session.active && useStoryBibleStore.getState().isCurrentOwner(owner);
    // Observe changes synchronously within this load's lifetime. The previous
    // owner's cleanup reset is not a toggle, and hydration must not count either.
    let isActiveChanged = false;
    const unsubscribe = useStoryBibleStore.subscribe((state, previous) => {
      if (state.owner === owner && !session.hydrated && state.isActive !== previous.isActive) {
        isActiveChanged = true;
      }
    });
    let retryTimeout;

    // Each lifecycle owns its queue and retries. A late A retry cannot replace B's debounce.
    const save = async (envelope) => {
      // Cleanup flushes before releasing ownership; an already replaced owner
      // must never flush or debounce its stale payload over the current one.
      if (!envelope || !session.engineId || !useStoryBibleStore.getState().isCurrentOwner(owner)) return;
      const { payload, revision } = envelope;
      const { scenesToSave, scenesJson } = buildScenesForSave(payload.scenes, session.lastSavedScenesJson);
      const metadataForSave = { ...payload.metadata };
      if (stripLegacyStyleFromSave) delete metadataForSave.style;
      // These snapshots are hydrated for reading, but their writers are the
      // Style Guide, not Story Bible autosave.
      delete metadataForSave.simpleStyleControls;
      try {
        await Auth.fetchWithIdToken(`/api/story-engine/${session.engineId}`, {
          method: "PUT",
          ...payload,
          metadata: metadataForSave,
          scenes: scenesToSave,
        });
        if (scenesToSave !== undefined) session.lastSavedScenesJson = scenesJson;
        if (session.latest?.revision === revision) session.latest = null;
        if (isCurrent() && session.revision === revision) {
          setSaved(true);
          setSaveError(false);
        }
      } catch (e) {
        let responseMessage = e?.message || "";
        try {
          responseMessage = (await e?.response?.json?.())?.message || responseMessage;
        } catch {
          // Keep the original message when the response is not JSON.
        }
        const errorInfo = errorMap.find((info) => typeof responseMessage === "string" && responseMessage.includes(info.matches)) || {
          message: "We are having trouble saving changes. Please check your internet connection to avoid losing work.",
          retry: true,
        };
        if (isCurrent() && session.revision === revision) setSaveError({ message: errorInfo.message, persistent: Boolean(errorInfo.persistent) });
        if (errorInfo.retry && isCurrent() && session.revision === revision) {
          clearTimeout(retryTimeout);
          retryTimeout = setTimeout(() => {
            retryTimeout = undefined;
            // A newer complete payload supersedes this failed write in the same lifecycle.
            if (isCurrent() && session.revision === revision) void save(envelope);
          }, 5000);
        }
      }
    };
    session.queue = debounce(save, 1000);
    const run = async () => {
      setIsLoadingSceneToWrite(true);
      try {
        const [results, featuredStylesResponse] = await Promise.all([Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {}), Auth.fetchWithIdToken("/api/storyEngine/style/featured-styles")]);

        if (!isCurrent()) return;
        if (!results?.storyEngine) {
          const error = new Error("Story engine payload missing storyEngine");
          Sentry.captureException(error, { tags: { feature: "story_bible_initial_load" }, extra: { projectId } });
          setSaveError("Failed to load story bible data");
          return;
        }

        const fetchedIsActive = typeof results.storyEngine.isActive === "undefined" ? null : results.storyEngine.isActive;
        const storyEngineData = results.storyEngine;
        const safeSeed = storyEngineData.seed ?? "";
        const safeCharacters = storyEngineData.characters ?? "";
        const safeSynopsis = storyEngineData.synopsis ?? "";
        const safeOutline = storyEngineData.outline ?? "";
        const safeScenes = storyEngineData.scenes ?? {};
        const safeMetadata = storyEngineData.metadata ?? {};

        // Consume the bridge value. Only clear it when the projectId matches
        // so a mount GET for a different project doesn't drop the original
        // project's pending value (SW-10355).
        const pendingSave = _pendingSave;
        if (pendingSave === undefined || pendingSave.projectId === projectId) {
          _pendingSave = undefined;
        }

        const fields = { seed: safeSeed, characters: safeCharacters, outline: safeOutline, scenes: safeScenes, synopsis: safeSynopsis, metadata: safeMetadata };
        session.lastPayload = { ...fields, isActive: fetchedIsActive };
        const activeValue = isActiveChanged ? useStoryBibleStore.getState().isActive : pendingSave?.projectId === projectId ? pendingSave.isActive : fetchedIsActive;
        session.engineId = storyEngineData.id;
        session.lastSavedScenesJson = serializeScenes(safeScenes);
        session.hydrated = true;
        store.hydrateOwner(owner, storyEngineData.id, { ...fields, isActive: activeValue });
        setFeaturedStyles(featuredStylesResponse.featuredStyles);
      } catch (error) {
        if (!isCurrent()) return;
        Sentry.captureException(error, { tags: { feature: "story_bible_initial_load" }, extra: { projectId } });
        setSaveError("Failed to load story bible data");
      } finally {
        if (isCurrent()) {
          setLoadingState(false);
          setIsLoadingSceneToWrite(false);
        }
      }
    };

    run();

    return () => {
      session.active = false;
      unsubscribe();
      clearTimeout(retryTimeout);
      if (session.latest && store.isCurrentOwner(owner)) {
        _pendingSave = { projectId, isActive: session.latest.payload.isActive };
      }
      session.queue.flush();
      store.releaseOwner(owner);
    };
  }, [projectId]);

  const forceSave = useCallback(() => {
    const session = sessionRef.current;
    if (session?.active) session.queue.flush();
  }, []);

  // Capture the complete payload while its originating load still owns the fields.
  useEffect(() => {
    const session = sessionRef.current;
    if (!session?.hydrated || owner !== session.owner || seed === null) return;
    const payload = { isActive, seed, characters, synopsis, outline, metadata, scenes };
    if (Object.keys(payload).every((key) => payload[key] === session.lastPayload?.[key])) return;
    session.lastPayload = payload;
    const envelope = { payload, revision: ++session.revision };
    session.latest = envelope;
    setSaved(false);
    session.queue(envelope);
  }, [owner, isActive, seed, characters, synopsis, outline, metadata, scenes]);

  return { loading, saved, loadingState, forceSave };
};
